import { Loader2 } from "lucide-react";
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import BaseDrawer from "@/shared/components/Drawer";
import { ACCOUNTING_DATE_BOUNDS } from "@/shared/configs/accountingDateBounds";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import { toastErrorIfNotForbidden } from "@/shared/utils/errorHandling";
import { accountingService } from "../../services/accounting.service";
import StockExpiryDateField from "./StockExpiryDateField";
import {
  CREDIT_UNITS_LOCKED_HINT, DEBIT_UNITS_LOCKED_HINT, getAmountSideLock, LOCKED_AMOUNT_INPUT_CLASS,
} from "./utils";
import type { StockEditorSaveOutcome, StockEditorSavePayload } from "./useMovementAllocationsState";
import type { LedgerAllocation, StockFamily, StockReference } from "../../types/accounting.types";

// Le back tague les familles Bons Plans avec la catégorie "E-commerce" (cf. ADM-706),
// mais côté Console cette section s'appelle "Bons plans" : on mappe l'affichage sans
// toucher à la valeur, qui pilote le filtre des familles.
const CATEGORY_DISPLAY_LABELS: Record<string, string> = { "E-commerce": "Bons plans" };

const getCategoryLabel = (category: string): string => CATEGORY_DISPLAY_LABELS[category] ?? category;

type StockAllocationEditorProps = {
  open: boolean
  onClose: () => void
  bankMovementId: string
  allocation: LedgerAllocation | null
  isBusy: boolean
  onSave: (payload: StockEditorSavePayload) => void | Promise<StockEditorSaveOutcome>
};

const StockAllocationEditor = ({
  open,
  onClose,
  bankMovementId,
  allocation,
  isBusy,
  onSave,
}: StockAllocationEditorProps) => {
  // ---------------------------------------------------------------------------
  // Local form state
  // ---------------------------------------------------------------------------
  const [date, setDate] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedFamilyId, setSelectedFamilyId] = useState("");
  const [selectedReferenceId, setSelectedReferenceId] = useState("");
  const [selectedSupplierId, setSelectedSupplierId] = useState("");
  const [debitQty, setDebitQty] = useState("");
  const [creditQty, setCreditQty] = useState("");
  const [stockExpiresOn, setStockExpiresOn] = useState("");

  // ---------------------------------------------------------------------------
  // Data loading state
  // ---------------------------------------------------------------------------
  const [categories, setCategories] = useState<string[]>([]);
  const [families, setFamilies] = useState<StockFamily[]>([]);
  const [references, setReferences] = useState<StockReference[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string, name: string }[]>([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [isSupplierPopoverOpen, setIsSupplierPopoverOpen] = useState(false);
  const supplierContainerRef = useRef<HTMLDivElement | null>(null);

  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingFamilies, setIsLoadingFamilies] = useState(false);
  const [isLoadingReferences, setIsLoadingReferences] = useState(false);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);

  // ---------------------------------------------------------------------------
  // Initialize form from allocation
  // ---------------------------------------------------------------------------
  const resetFormFromAllocation = useCallback(() => {
    if (!allocation) return;
    setDate(allocation.date ?? "");
    setDebitQty(allocation.stockDebitQuantity !== null ? String(allocation.stockDebitQuantity) : "");
    setCreditQty(allocation.stockCreditQuantity !== null ? String(allocation.stockCreditQuantity) : "");
    setSelectedCategory(allocation.stockActivity ?? "");
    setSelectedFamilyId("");
    setSelectedReferenceId(allocation.stockReferenceId ?? "");
    setSelectedSupplierId(allocation.supplierId ?? "");
    setSupplierSearch(allocation.supplierName ?? "");
    setStockExpiresOn(allocation.stockExpiresOn ?? "");
  }, [allocation]);

  useEffect(() => {
    if (!open || !allocation) return;
    resetFormFromAllocation();
  }, [open, allocation?.id]);

  // ---------------------------------------------------------------------------
  // Load categories on open
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open || !bankMovementId) return;
    let cancelled = false;
    setIsLoadingCategories(true);
    accountingService.listStockCategories(bankMovementId)
      .then((data) => {
        if (!cancelled) setCategories(data);
      })
      .catch((error) => {
        if (!cancelled) toastErrorIfNotForbidden(error, "Erreur lors du chargement des catégories stock");
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCategories(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, bankMovementId]);

  // ---------------------------------------------------------------------------
  // Load families when category changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open || !bankMovementId || !selectedCategory) {
      setFamilies([]);
      return;
    }
    let cancelled = false;
    setIsLoadingFamilies(true);
    accountingService.listStockFamilies(bankMovementId, selectedCategory)
      .then((data) => {
        if (!cancelled) setFamilies(data);
      })
      .catch((error) => {
        if (!cancelled) toastErrorIfNotForbidden(error, "Erreur lors du chargement des familles");
      })
      .finally(() => {
        if (!cancelled) setIsLoadingFamilies(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, bankMovementId, selectedCategory]);

  // Auto-select family if allocation has stockFamily info
  useEffect(() => {
    if (!allocation?.stockFamily || families.length === 0) return;
    const match = families.find((f) => f.name === allocation.stockFamily);
    if (match && !selectedFamilyId) {
      setSelectedFamilyId(match.id);
    }
  }, [families, allocation?.stockFamily]);

  // ---------------------------------------------------------------------------
  // Load references when family changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open || !bankMovementId || !selectedFamilyId) {
      setReferences([]);
      return;
    }
    let cancelled = false;
    setIsLoadingReferences(true);
    accountingService.listStockReferences(bankMovementId, selectedFamilyId)
      .then((data) => {
        if (!cancelled) setReferences(data);
      })
      .catch((error) => {
        if (!cancelled) toastErrorIfNotForbidden(error, "Erreur lors du chargement des références");
      })
      .finally(() => {
        if (!cancelled) setIsLoadingReferences(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, bankMovementId, selectedFamilyId]);

  // ---------------------------------------------------------------------------
  // Load suppliers (search-based)
  // ---------------------------------------------------------------------------
  const supplierSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadSuppliers = useCallback((search: string) => {
    setIsLoadingSuppliers(true);
    accountingService.searchSuppliers(search)
      .then((suppliers) => {
        setSuppliers(suppliers);
      })
      .catch(() => {})
      .finally(() => setIsLoadingSuppliers(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    loadSuppliers("");
  }, [open, loadSuppliers]);

  const handleSupplierSearchChange = (value: string) => {
    setSupplierSearch(value);
    if (supplierSearchTimerRef.current) clearTimeout(supplierSearchTimerRef.current);
    supplierSearchTimerRef.current = setTimeout(() => loadSuppliers(value), 300);
  };

  // Close supplier popover on outside click
  useEffect(() => {
    if (!isSupplierPopoverOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!supplierContainerRef.current) return;
      if (!supplierContainerRef.current.contains(event.target as Node)) {
        setIsSupplierPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isSupplierPopoverOpen]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------
  const selectedReference = useMemo(
    () => references.find((r) => r.id === selectedReferenceId) ?? null,
    [references, selectedReferenceId],
  );

  const selectedSupplierName = useMemo(() => {
    if (supplierSearch) return supplierSearch;
    const found = suppliers.find((s) => s.id === selectedSupplierId);
    return found?.name ?? "";
  }, [suppliers, selectedSupplierId, supplierSearch]);

  const formatEuro = (value: number) =>
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value);

  const hasQuantity = (debitQty !== "" && Number(debitQty) > 0) || (creditQty !== "" && Number(creditQty) > 0);
  const { isDebitLocked, isCreditLocked } = getAmountSideLock(debitQty, creditQty);
  const canSave = !!selectedReferenceId && hasQuantity;
  const isLinkedToOperation = !!allocation?.activityOperationId;
  const canDateLot = !isLinkedToOperation && Number(creditQty) > 0 && !(Number(debitQty) > 0);

  // ---------------------------------------------------------------------------
  // Save handler
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    const outcome = await onSave({
      date: date || null,
      stockReferenceId: selectedReferenceId || null,
      supplierId: selectedSupplierId || null,
      stockDebitQuantity: debitQty ? Number(debitQty) : null,
      stockCreditQuantity: creditQty ? Number(creditQty) : null,
      stockExpiresOn: canDateLot && stockExpiresOn ? stockExpiresOn : null,
    });
    // Refus du serveur : rien n'est parti en base, le drawer reste ouvert pour
    // corriger. Il doit repartir des quantités enregistrées, sinon il garde à
    // l'écran une saisie refusée qui a tout l'air d'être enregistrée.
    if (outcome === "rejected") resetFormFromAllocation();
  };

  // ---------------------------------------------------------------------------
  // Reset when closed
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open) {
      setCategories([]);
      setFamilies([]);
      setReferences([]);
      setSuppliers([]);
      setSelectedCategory("");
      setSelectedFamilyId("");
      setSelectedReferenceId("");
      setSelectedSupplierId("");
      setSupplierSearch("");
      setDate("");
      setDebitQty("");
      setCreditQty("");
      setStockExpiresOn("");
    }
  }, [open]);

  return (
    <BaseDrawer
      drawerId="DRW-STOCK"
      open={open}
      onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}
      isFirstLevel={false}
      onBack={onClose}
      width="893px"
      zIndex={65}
      bodyClassName="[&_*]:select-text"
      title="📦 Écriture stock"
      description="Sélectionnez la catégorie, famille et référence pour cette écriture stock."
    >
      <div className="rounded-md border bg-white p-3 mb-4 space-y-3 [&_label]:!text-[12px] [&_label]:font-normal [&_label]:leading-tight [&_input]:!text-[12px] [&_input]:md:!text-[12px] [&_input]:leading-tight [&_select]:!text-[12px]">
        {/* Date */}
        <div className="space-y-1">
          <label className="text-[12px] leading-tight text-slate-600">Date</label>
          <Input
            type="date"
            min={ACCOUNTING_DATE_BOUNDS.min}
            max={ACCOUNTING_DATE_BOUNDS.max}
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="h-8 !text-[12px] md:!text-[12px] leading-tight max-w-[200px]"
            disabled={isBusy}
          />
        </div>

        {/* Catégorie */}
        <div className="space-y-1">
          <label className="text-[12px] leading-tight text-slate-600">
            Catégorie
            {isLoadingCategories && <Loader2 className="inline h-3 w-3 ml-1 animate-spin" />}
          </label>
          <select
            className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px]"
            value={selectedCategory}
            onChange={(event) => {
              setSelectedCategory(event.target.value);
              setSelectedFamilyId("");
              setSelectedReferenceId("");
            }}
            disabled={isBusy || isLoadingCategories}
          >
            <option value="">— Choisir une catégorie —</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
            ))}
          </select>
        </div>

        {/* Famille */}
        <div className="space-y-1">
          <label className="text-[12px] leading-tight text-slate-600">
            Famille
            {isLoadingFamilies && <Loader2 className="inline h-3 w-3 ml-1 animate-spin" />}
          </label>
          <select
            className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px]"
            value={selectedFamilyId}
            onChange={(event) => {
              setSelectedFamilyId(event.target.value);
              setSelectedReferenceId("");
            }}
            disabled={isBusy || isLoadingFamilies || !selectedCategory}
          >
            <option value="">— Choisir une famille —</option>
            {families.map((fam) => (
              <option key={fam.id} value={fam.id}>{fam.name}</option>
            ))}
          </select>
        </div>

        {/* Référence */}
        <div className="space-y-1">
          <label className="text-[12px] leading-tight text-slate-600">
            Référence
            {isLoadingReferences && <Loader2 className="inline h-3 w-3 ml-1 animate-spin" />}
          </label>
          <select
            className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px]"
            value={selectedReferenceId}
            onChange={(event) => setSelectedReferenceId(event.target.value)}
            disabled={isBusy || isLoadingReferences || !selectedFamilyId}
          >
            <option value="">— Choisir une référence —</option>
            {references.map((ref) => (
              <option key={ref.id} value={ref.id}>
                {ref.name}
                {ref.stockValue !== null ? ` (${ref.stockValue} €)` : ""}
              </option>
            ))}
          </select>
          {/* eslint-disable-next-line @typescript-eslint/prefer-optional-chain */}
          {selectedReference !== null && selectedReference.stockValue !== null && (
            <div className="text-[11px] text-slate-500">
              Valeur unitaire :
              {" "}
              <span className="font-medium">{formatEuro(selectedReference.stockValue)}</span>
            </div>
          )}
        </div>

        {/* Fournisseur */}
        <div className="space-y-1">
          <label className="text-[12px] leading-tight text-slate-600">
            Fournisseur
            {isLoadingSuppliers && <Loader2 className="inline h-3 w-3 ml-1 animate-spin" />}
          </label>
          <div ref={supplierContainerRef} className="relative">
            <Input
              value={supplierSearch}
              placeholder="Rechercher un fournisseur"
              onFocus={() => setIsSupplierPopoverOpen(true)}
              onClick={() => setIsSupplierPopoverOpen(true)}
              onChange={(event) => {
                handleSupplierSearchChange(event.target.value);
                setIsSupplierPopoverOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") setIsSupplierPopoverOpen(false);
              }}
              className="h-8 !text-[12px] md:!text-[12px] leading-tight"
              disabled={isBusy}
            />
            {isSupplierPopoverOpen && (
              <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-md p-1">
                <div className="max-h-48 overflow-y-auto overscroll-contain">
                  {suppliers.length === 0 && !isLoadingSuppliers && (
                    <div className="px-2 py-1.5 text-[12px] text-slate-500">Aucun fournisseur trouvé</div>
                  )}
                  {suppliers.map((supplier) => (
                    <button
                      key={supplier.id}
                      type="button"
                      className="w-full text-left px-2 py-1.5 text-[12px] rounded hover:bg-slate-100"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setSelectedSupplierId(supplier.id);
                        setSupplierSearch(supplier.name);
                        setIsSupplierPopoverOpen(false);
                      }}
                    >
                      {supplier.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {selectedSupplierId && (
            <div className="flex items-center gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[12px] text-emerald-900">
                <span className="truncate">{selectedSupplierName || "Fournisseur sélectionné"}</span>
                <button
                  type="button"
                  className="inline-flex items-center text-emerald-700 hover:text-emerald-900"
                  onClick={() => {
                    setSelectedSupplierId("");
                    setSupplierSearch("");
                  }}
                  disabled={isBusy}
                >
                  ✕
                </button>
              </span>
            </div>
          )}
        </div>

        {/* Quantités */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-[12px] leading-tight text-slate-600">Nombre d&apos;unités débitées</label>
            <Input
              aria-label="Nombre d'unités débitées"
              type="number"
              min={0}
              step="any"
              value={debitQty}
              onChange={(event) => setDebitQty(event.target.value)}
              className={`h-8 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isDebitLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
              placeholder="0"
              disabled={isBusy}
              readOnly={isDebitLocked}
              aria-disabled={isDebitLocked}
              title={isDebitLocked ? DEBIT_UNITS_LOCKED_HINT : undefined}
            />
          </div>
          <div className="space-y-1">
            <label className="text-[12px] leading-tight text-slate-600">Nombre d&apos;unités créditées</label>
            <Input
              aria-label="Nombre d'unités créditées"
              type="number"
              min={0}
              step="any"
              value={creditQty}
              onChange={(event) => setCreditQty(event.target.value)}
              className={`h-8 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isCreditLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
              placeholder="0"
              disabled={isBusy}
              readOnly={isCreditLocked}
              aria-disabled={isCreditLocked}
              title={isCreditLocked ? CREDIT_UNITS_LOCKED_HINT : undefined}
            />
          </div>
        </div>

        {!isLinkedToOperation && (
          <StockExpiryDateField
            value={stockExpiresOn}
            onChange={setStockExpiresOn}
            isEnabled={canDateLot}
            isBusy={isBusy}
          />
        )}

        {/* Récapitulatif valorisation stock (valeurs persistées uniquement) */}
        {allocation !== null && allocation.stockUnitValue !== null && (!!allocation.stockDebitAmount || !!allocation.stockCreditAmount) && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 space-y-1">
            <div className="text-[12px] font-medium text-slate-700">Valorisation stock</div>
            <div className="space-y-0.5 text-[12px] text-slate-600">
              <div>
                <span className="text-slate-500">Valeur unitaire :</span>
                {" "}
                <span className="font-medium text-slate-800">{formatEuro(allocation.stockUnitValue)}</span>
              </div>
              {!!allocation.stockDebitAmount && (
                <div>
                  <span className="text-slate-500">Débit stock :</span>
                  {" "}
                  <span className="font-medium text-slate-800">
                    {allocation.stockDebitQuantity}
                    {" "}
                    x
                    {" "}
                    {formatEuro(allocation.stockUnitValue)}
                    {" "}
                    =
                    {" "}
                    {formatEuro(allocation.stockDebitAmount)}
                  </span>
                </div>
              )}
              {!!allocation.stockCreditAmount && (
                <div>
                  <span className="text-slate-500">Crédit stock :</span>
                  {" "}
                  <span className="font-medium text-slate-800">
                    {allocation.stockCreditQuantity}
                    {" "}
                    x
                    {" "}
                    {formatEuro(allocation.stockUnitValue)}
                    {" "}
                    =
                    {" "}
                    {formatEuro(allocation.stockCreditAmount)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isBusy}
            className="h-8 text-[12px]"
          >
            Annuler
          </Button>
          <Button
            type="button"
            variant="success"
            onClick={() => void handleSave()}
            disabled={isBusy || !canSave}
            className="h-8 text-[12px]"
          >
            Enregistrer
          </Button>
        </div>
      </div>
    </BaseDrawer>
  );
};

export default StockAllocationEditor;
